<template>
	<div class="inputFieldContainer">
		<!--	Wrapped the Input with a clientOnly element to remove hydration console logs, not sure why this is happening	-->
		<ClientOnly>
			<label
				:for="id"
				class="inputFieldLabel"
				:class="labelColor"
				>{{ label }}
				<FormRequiredAsterisk :required="required" />
			</label>
			<input
				:id="id"
				:autocomplete="autocomplete"
				type="text"
				:placeholder="placeholder"
				v-model="value"
				class="inputField"
				:class="{ inputFieldError: hasError }"
				:disabled="disabled"
				:maxlength="maxLength"
				@input="input"
				@blur="blur"
				@change="changed" />
			<label
				:for="id"
				v-if="hasError && errorMessage"
				class="inputFieldErrorLabel"
				>{{ errorMessage }}</label
			>
		</ClientOnly>
	</div>
</template>

<script setup>
	import { isNonEmpty } from "~/assets/helpers/fieldValidators"

	const runtimeConfig = useRuntimeConfig()

	const { id, hasError, initialValue, country } = defineProps({
		id: {
			type: String,
			default: "inputField",
		},
		label: {
			type: String,
			default: "Field Label",
		},
		labelColor: {
			type: String,
			default: "text-primary",
		},
		placeholder: {
			type: String,
			default: "",
		},
		required: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		hasError: {
			type: Boolean,
			default: false,
		},
		errorMessage: {
			type: String,
			default: "",
		},
		initialValue: {
			type: String,
			default: "",
		},
		country: {
			type: String,
			default: "CAN",
		},
	})

	const autocomplete = runtimeConfig.public.IS_KIOSK_APP ? "dsadsa" : ""

	const { $postalCodeService } = useNuxtApp()
	const emits = defineEmits(["changed", "blur"])
	const value = ref(initialValue)
	const maxLength = ref(99)

	switch (country) {
		case "CAN":
			maxLength.value = 7
			break
		case "USA":
			maxLength.value = 10
			break
		default:
			maxLength.value = 99
			break
	}

	const formatField = (value) => {
		switch (country) {
			case "CAN":
				// Remove any non-alphanumeric characters
				let postalCode = value.replace(/[^A-Za-z0-9]/g, "")

				// Insert the space if the postal code is longer than 3 characters
				if (postalCode.length > 3) {
					postalCode = postalCode.slice(0, 3) + " " + postalCode.slice(3)
				}

				// Update the input value with the formatted postal code
				return postalCode
			case "USA":
				// Remove any non-numeric characters
				let zipCode = value.replace(/\D/g, "")

				// Insert the hyphen if the ZIP code is longer than 5 characters
				if (zipCode.length > 5) {
					zipCode = zipCode.slice(0, 5) + "-" + zipCode.slice(5)
				}

				// Update the input value with the formatted ZIP code
				return zipCode
			default:
				return value
		}
	}

	const input = () => {
		value.value = formatField(value.value.toUpperCase())
	}

	const changed = () => {
		emits("changed", { id: id, value: value.value })
	}

	const blur = () => {
		emits("blur", { id: id, value: value.value })
	}

	const isFieldValid = () => {
		if (isNonEmpty(value.value)) {
			const regex = $postalCodeService.$getPostalCodePattern(country)
			return regex.test(value.value)
		}
		return false
	}

	onMounted(() => {
		value.value = formatField(initialValue.toUpperCase())
	})

	defineExpose({
		isFieldValid,
	})
</script>

<style scoped></style>
